"use client";

import {
  getHistory,
  saveHistory,
  syncProjectCacheInventory,
} from "@/lib/aicheck/cache-inventory";
import {
  CONTACT_TOPICS_VERSION,
  getContactTopicsKey,
  getDismissedKey,
  getGraphExpandedKey,
  getMergeCheckResultCacheKey,
  getProjectDataSourceCacheKey,
  getResultCacheKey,
  MERGE_CHECK_RESULT_PREFIX,
  PROJECT_DATA_SOURCE_PREFIX,
  RESULTS_CACHE_VERSION,
} from "@/lib/aicheck/cache-keys";
import {
  FREEFORM_RESULT_CACHE_PREFIX,
  FREEFORM_RESULT_CACHE_VERSION,
  type FreeformReviewResultCacheEntry,
  getFreeformReviewResultCacheKey,
  omitFreeformReviewProjectData,
  readLatestFreeformReviewResultCache,
} from "@/lib/aicheck/freeform-result-cache";
import {
  createSingleJsonZipBlob,
  isZipArchiveFile,
  readSingleJsonFromZipFile,
} from "@/lib/aicheck/json-zip";
import { buildMergeStageCheckCacheKey } from "@/lib/aicheck/merge-check-cache";
import { isExternalProjectDataSource } from "@/lib/aicheck/project-data-source";
import { projectTitleFromRunResult } from "@/lib/aicheck/project-title";
import type { ProjectDataSourceCacheEntry } from "@/lib/project-data-converters/client-cache";
import {
  buildProjectDataSourceDetailsKey,
  type ProjectDataSourceDetailEntry,
  readProjectDataSourceDetails,
  writeProjectDataSourceDetails,
} from "@/lib/project-data-converters/client-detail-store";
import type {
  AICheckRunHistoryEntry,
  AICheckRunResult,
  MergeCheckCacheMetadata,
  OutputLanguage,
  ProjectDataSourceDescriptor,
} from "@/types/aicheck";

const BUNDLE_FORMAT = "xperisus-aicheck-share-bundle";
const BUNDLE_VERSION = 2;

interface BundleStorageEntry {
  key: string;
  value: string;
}

export interface AICheckShareBundle {
  format: typeof BUNDLE_FORMAT;
  version: typeof BUNDLE_VERSION;
  exportedAt: string;
  projectId: number;
  language: OutputLanguage;
  result: AICheckRunResult;
  source?: ProjectDataSourceDescriptor;
  mergeCheckCache?: MergeCheckCacheMetadata;
  freeformReviewResult?: FreeformReviewResultCacheEntry | null;
  storageEntries: BundleStorageEntry[];
  sourceDetails: ProjectDataSourceDetailEntry[];
  historyEntry: AICheckRunHistoryEntry;
}

export interface AICheckShareBundleRestoreResult {
  projectId: number;
  language: OutputLanguage;
  restoredEntryCount: number;
  restoredDetailCount: number;
  result: AICheckRunResult;
  mergeCheckCache?: MergeCheckCacheMetadata;
  freeformReviewResult?: FreeformReviewResultCacheEntry | null;
}

export interface AICheckShareBundleOverwrite {
  key: string;
  label: string;
  storage: "localStorage" | "IndexedDB";
}

export interface AICheckShareBundleImportPreview {
  fileName: string;
  projectId: number;
  language: OutputLanguage;
  projectName: string;
  sourceLabel?: string;
  completedAt: string;
  exportedAt: string;
  storageEntryCount: number;
  sourceDetailCount: number;
  freeformReviewResultCount: number;
  overwriteCount: number;
  overwrites: AICheckShareBundleOverwrite[];
}

export interface AICheckShareBundleImportOptions {
  overwrite?: boolean;
}

function langKey(projectId: number): string {
  return `aicheck:lang:${projectId}`;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function sourceCacheKey(
  source: ProjectDataSourceDescriptor | undefined,
): string | null {
  const cacheKey = source?.origin?.cacheKey;
  if (!cacheKey || cacheKey.startsWith("merge:")) return null;
  return getProjectDataSourceCacheKey(cacheKey);
}

function mergeCacheKey(
  source: ProjectDataSourceDescriptor | undefined,
): string | null {
  const cacheKey = source?.origin?.cacheKey;
  return cacheKey?.startsWith("merge:")
    ? getMergeCheckResultCacheKey(cacheKey)
    : null;
}

function buildHistoryEntry(result: AICheckRunResult): AICheckRunHistoryEntry {
  const source = result.projectDataSource;
  return {
    projectId: result.projectId,
    projectTitle: projectTitleFromRunResult(result),
    runAt: result.completedAt,
    status: result.status,
    language: result.language,
    sourceLabel: source?.label,
    sourceKind: source?.kind,
    sourceCacheKey: source?.origin?.cacheKey,
    usesExternalProjectData: isExternalProjectDataSource(source),
    aiResultCount: result.aiResults.length,
    consistencyResultCount: result.consistencyResults.length,
    passCount:
      result.aiResults.filter((item) => item.status === "pass").length +
      result.consistencyResults.filter((item) => item.passed).length,
    warningCount:
      result.aiResults.filter((item) => item.status === "warning").length +
      result.consistencyResults.filter(
        (item) => !item.passed && item.severity === "warning",
      ).length,
    failCount:
      result.aiResults.filter(
        (item) => item.status === "fail" || item.status === "error",
      ).length +
      result.consistencyResults.filter(
        (item) => !item.passed && item.severity === "error",
      ).length,
  };
}

function sourceDetailsFromStorageValue(value: string): {
  value: string;
  details?: ProjectDataSourceDetailEntry;
  detailsKey?: string;
} {
  let parsed: { v?: number; data?: Partial<ProjectDataSourceCacheEntry> };
  try {
    parsed = JSON.parse(value) as {
      v?: number;
      data?: Partial<ProjectDataSourceCacheEntry>;
    };
  } catch {
    return { value };
  }

  const data = parsed.data;
  if (!data?.sourceKey || !data.source) return { value };

  const warningDetails = Array.isArray(data.warningDetails)
    ? data.warningDetails.filter(
        (item): item is string => typeof item === "string",
      )
    : undefined;
  const hasDetailPayload = Boolean(
    data.conversionReport ||
      data.provenance ||
      (warningDetails && warningDetails.length > 0),
  );
  const detailsKey =
    data.detailsKey ?? buildProjectDataSourceDetailsKey(data.sourceKey);
  const sanitizedData = { ...data };
  delete sanitizedData.conversionReport;
  delete sanitizedData.provenance;
  delete sanitizedData.warningDetails;
  if (data.hasDetails || hasDetailPayload) {
    sanitizedData.detailsKey = detailsKey;
    sanitizedData.hasDetails = true;
  }

  return {
    value: JSON.stringify({ ...parsed, data: sanitizedData }),
    detailsKey: sanitizedData.hasDetails ? detailsKey : undefined,
    details: hasDetailPayload
      ? {
          detailsKey,
          sourceKey: data.sourceKey,
          conversionId: data.conversionId,
          source: data.source,
          conversionReport: data.conversionReport,
          provenance: data.provenance,
          warningDetails,
          cachedAt: data.cachedAt ?? new Date().toISOString(),
        }
      : undefined,
  };
}

function addStorageEntry(
  entries: BundleStorageEntry[],
  sourceDetails: ProjectDataSourceDetailEntry[],
  key: string | null,
): void {
  if (!key || entries.some((entry) => entry.key === key)) return;
  const value = readStorage(key);
  if (value == null) return;
  const sanitized = key.startsWith(PROJECT_DATA_SOURCE_PREFIX)
    ? sourceDetailsFromStorageValue(value)
    : { value };
  entries.push({ key, value: sanitized.value });
  if (sanitized.details) sourceDetails.push(sanitized.details);
}

function setStorageEntry(
  entries: BundleStorageEntry[],
  key: string,
  value: string,
): void {
  const existing = entries.find((entry) => entry.key === key);
  if (existing) {
    existing.value = value;
  } else {
    entries.push({ key, value });
  }
}

function addSourceEntry(
  entries: BundleStorageEntry[],
  sourceDetails: ProjectDataSourceDetailEntry[],
  source: ProjectDataSourceDescriptor | undefined,
): void {
  addStorageEntry(entries, sourceDetails, sourceCacheKey(source));
  addStorageEntry(entries, sourceDetails, mergeCacheKey(source));
}

function addMergeStageEntries(
  entries: BundleStorageEntry[],
  sourceDetails: ProjectDataSourceDetailEntry[],
  metadata: MergeCheckCacheMetadata | undefined,
): void {
  if (!metadata?.mergeCacheKey) return;
  const rootMergeCacheKey =
    metadata.rootMergeCacheKey ??
    (metadata.stage ? null : metadata.mergeCacheKey);
  addStorageEntry(
    entries,
    sourceDetails,
    getMergeCheckResultCacheKey(metadata.mergeCacheKey),
  );
  if (!rootMergeCacheKey) return;
  addStorageEntry(
    entries,
    sourceDetails,
    getMergeCheckResultCacheKey(rootMergeCacheKey),
  );
  for (const stage of ["base", "candidate", "merged"] as const) {
    const stageKey =
      stage === "merged"
        ? rootMergeCacheKey
        : buildMergeStageCheckCacheKey(rootMergeCacheKey, stage);
    addStorageEntry(
      entries,
      sourceDetails,
      getMergeCheckResultCacheKey(stageKey),
    );
  }
}

function freeformReviewStorageKey(
  entry: FreeformReviewResultCacheEntry,
  fallback: {
    projectId: number;
    source?: ProjectDataSourceDescriptor;
  },
): string {
  return getFreeformReviewResultCacheKey({
    projectId: entry.projectId || fallback.projectId,
    language: entry.language,
    source: entry.source ?? fallback.source,
  });
}

function freeformReviewResultFromStorageEntry(
  entry: BundleStorageEntry,
): FreeformReviewResultCacheEntry | null {
  if (!entry.key.startsWith(FREEFORM_RESULT_CACHE_PREFIX)) return null;
  try {
    const parsed = JSON.parse(entry.value) as {
      v?: number;
      data?: FreeformReviewResultCacheEntry;
    };
    if (
      parsed?.v !== FREEFORM_RESULT_CACHE_VERSION ||
      !parsed.data?.result ||
      (parsed.data.language !== "en" && parsed.data.language !== "ja")
    ) {
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function freeformReviewResultFromBundle(
  bundle: AICheckShareBundle,
): FreeformReviewResultCacheEntry | null {
  if (bundle.freeformReviewResult?.result) {
    return omitFreeformReviewProjectData(bundle.freeformReviewResult);
  }
  for (const entry of bundle.storageEntries ?? []) {
    const result = freeformReviewResultFromStorageEntry(entry);
    if (result) return omitFreeformReviewProjectData(result);
  }
  return null;
}

/**
 * Strips the legacy `projectData` field from a serialized freeform review
 * result cache entry so importing an older bundle never writes it back into
 * localStorage. Non-freeform / unparsable values are returned unchanged.
 */
function stripFreeformProjectDataFromStorageValue(value: string): string {
  try {
    const parsed = JSON.parse(value) as {
      v?: number;
      data?: FreeformReviewResultCacheEntry;
    };
    if (!parsed?.data || parsed.data.projectData === undefined) return value;
    return JSON.stringify({
      ...parsed,
      data: omitFreeformReviewProjectData(parsed.data),
    });
  } catch {
    return value;
  }
}

function detailsKeyFromStorageEntry(entry: BundleStorageEntry): string | null {
  if (!entry.key.startsWith(PROJECT_DATA_SOURCE_PREFIX)) return null;
  try {
    const parsed = JSON.parse(entry.value) as {
      data?: Partial<ProjectDataSourceCacheEntry>;
    };
    const data = parsed.data;
    if (!data?.sourceKey || !data.hasDetails) return null;
    return data.detailsKey ?? buildProjectDataSourceDetailsKey(data.sourceKey);
  } catch {
    return null;
  }
}

async function collectStoredSourceDetails(
  entries: BundleStorageEntry[],
  sourceDetails: ProjectDataSourceDetailEntry[],
): Promise<ProjectDataSourceDetailEntry[]> {
  const details: ProjectDataSourceDetailEntry[] = [];
  const seen = new Set<string>();
  for (const detail of sourceDetails) {
    if (seen.has(detail.detailsKey)) continue;
    details.push(detail);
    seen.add(detail.detailsKey);
  }
  for (const entry of entries) {
    const detailsKey = detailsKeyFromStorageEntry(entry);
    if (!detailsKey || seen.has(detailsKey)) continue;
    try {
      const stored = await readProjectDataSourceDetails(detailsKey);
      if (stored) {
        details.push(stored);
        seen.add(stored.detailsKey);
      }
    } catch (error) {
      console.warn(
        "[aicheck-share-bundle] Failed to read source details",
        error,
      );
    }
  }
  return details;
}

function isAllowedBundleKey(key: string): boolean {
  return (
    key.startsWith("aicheck:results:") ||
    key.startsWith("aicheck:contact_topics:") ||
    key.startsWith("aicheck:dismissed:") ||
    key.startsWith("aicheck:graph-expanded:") ||
    key.startsWith(PROJECT_DATA_SOURCE_PREFIX) ||
    key.startsWith(MERGE_CHECK_RESULT_PREFIX) ||
    key.startsWith(FREEFORM_RESULT_CACHE_PREFIX) ||
    key.startsWith("aicheck:lang:")
  );
}

function mergeHistoryEntry(entry: AICheckRunHistoryEntry): void {
  const existing = getHistory().filter(
    (item) =>
      !(item.projectId === entry.projectId && item.language === entry.language),
  );
  saveHistory([entry, ...existing]);
}

function projectNameFromResult(result: AICheckRunResult): string {
  const derivedTitle = projectTitleFromRunResult(result);
  if (derivedTitle !== `Project ${result.projectId}`) return derivedTitle;

  const project = asRecord(result.projectData?.project);
  const directName =
    asString(project.name) ??
    asString(project.title) ??
    asString(project.project_name) ??
    asString(project.tour_name) ??
    asString(project.itinerary_name);
  if (directName) return directName;

  const customerNames = Array.isArray(project.customer_projects)
    ? project.customer_projects
        .map((item) => asString(asRecord(asRecord(item).customer).name))
        .filter((item): item is string => item != null)
    : [];
  const tourCode = asString(project.tour_code);
  const travelStart = asString(project.travel_start_date);
  const travelEnd = asString(project.travel_end_date);
  const dateRange =
    travelStart && travelEnd
      ? `${travelStart} - ${travelEnd}`
      : (travelStart ?? travelEnd);
  const parts = [
    customerNames.slice(0, 2).join(", "),
    tourCode ? `Tour ${tourCode}` : null,
    dateRange,
  ].filter((item): item is string => Boolean(item));
  if (parts.length > 0) return parts.join(" / ");
  return result.projectDataSource?.label ?? `Project #${result.projectId}`;
}

function buildBundleRestorePlan(bundle: AICheckShareBundle): {
  storageEntries: BundleStorageEntry[];
  sourceDetails: ProjectDataSourceDetailEntry[];
} {
  const storageEntries = new Map<string, string>();
  const sourceDetails = new Map<string, ProjectDataSourceDetailEntry>();

  for (const detail of bundle.sourceDetails ?? []) {
    if (detail?.detailsKey && detail.sourceKey && detail.source) {
      sourceDetails.set(detail.detailsKey, detail);
    }
  }

  for (const entry of bundle.storageEntries) {
    if (!entry || !isAllowedBundleKey(entry.key)) continue;
    if (typeof entry.value !== "string") continue;
    const sanitized = entry.key.startsWith(PROJECT_DATA_SOURCE_PREFIX)
      ? sourceDetailsFromStorageValue(entry.value)
      : { value: entry.value };
    if (sanitized.details) {
      sourceDetails.set(sanitized.details.detailsKey, sanitized.details);
    }
    const value = entry.key.startsWith(FREEFORM_RESULT_CACHE_PREFIX)
      ? stripFreeformProjectDataFromStorageValue(sanitized.value)
      : sanitized.value;
    storageEntries.set(entry.key, value);
  }

  storageEntries.set(
    getResultCacheKey(bundle.projectId, bundle.language),
    JSON.stringify({ v: RESULTS_CACHE_VERSION, data: bundle.result }),
  );
  storageEntries.set(langKey(bundle.projectId), bundle.language);

  const freeformReviewResult = freeformReviewResultFromBundle(bundle);
  if (freeformReviewResult) {
    storageEntries.set(
      freeformReviewStorageKey(freeformReviewResult, {
        projectId: bundle.projectId,
        source: bundle.source ?? bundle.result.projectDataSource,
      }),
      JSON.stringify({
        v: FREEFORM_RESULT_CACHE_VERSION,
        data: freeformReviewResult,
      }),
    );
  }

  return {
    storageEntries: [...storageEntries].map(([key, value]) => ({ key, value })),
    sourceDetails: [...sourceDetails.values()],
  };
}

function labelBundleStorageKey(key: string): string {
  if (key.startsWith("aicheck:results:")) return "Check result cache";
  if (key.startsWith("aicheck:contact_topics:")) {
    return "Contact topics cache";
  }
  if (key.startsWith("aicheck:dismissed:")) return "Dismissed checks cache";
  if (key.startsWith("aicheck:graph-expanded:")) {
    return "Progress graph state";
  }
  if (key.startsWith(PROJECT_DATA_SOURCE_PREFIX)) {
    return "Imported data source cache";
  }
  if (key.startsWith(MERGE_CHECK_RESULT_PREFIX)) {
    return "Merge check result cache";
  }
  if (key.startsWith(FREEFORM_RESULT_CACHE_PREFIX)) {
    return "Freeform Review result cache";
  }
  if (key.startsWith("aicheck:lang:")) return "Language preference";
  return "AI check cache";
}

function stringifyComparable(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

async function findBundleOverwrites(
  bundle: AICheckShareBundle,
): Promise<AICheckShareBundleOverwrite[]> {
  const plan = buildBundleRestorePlan(bundle);
  const overwrites: AICheckShareBundleOverwrite[] = [];

  for (const entry of plan.storageEntries) {
    const current = readStorage(entry.key);
    if (current != null && current !== entry.value) {
      overwrites.push({
        key: entry.key,
        label: labelBundleStorageKey(entry.key),
        storage: "localStorage",
      });
    }
  }

  for (const detail of plan.sourceDetails) {
    const current = await readProjectDataSourceDetails(detail.detailsKey).catch(
      () => null,
    );
    const currentComparable = current ? stringifyComparable(current) : null;
    const nextComparable = stringifyComparable(detail);
    if (
      current &&
      (currentComparable == null ||
        nextComparable == null ||
        currentComparable !== nextComparable)
    ) {
      overwrites.push({
        key: detail.detailsKey,
        label: "Imported data details",
        storage: "IndexedDB",
      });
    }
  }

  return overwrites;
}

export async function createAICheckShareBundle(params: {
  result: AICheckRunResult;
  activeSource?: {
    source?: ProjectDataSourceDescriptor;
    mergeCheckCache?: MergeCheckCacheMetadata;
  } | null;
}): Promise<AICheckShareBundle> {
  const { result, activeSource } = params;
  const entries: BundleStorageEntry[] = [];
  const sourceDetails: ProjectDataSourceDetailEntry[] = [];
  const resultKey = getResultCacheKey(result.projectId, result.language);
  const latestFreeformReviewResult = readLatestFreeformReviewResultCache({
    projectId: result.projectId,
    language: result.language,
    source: activeSource?.source ?? result.projectDataSource,
  });
  const freeformReviewResult = latestFreeformReviewResult
    ? omitFreeformReviewProjectData(latestFreeformReviewResult)
    : null;

  addStorageEntry(
    entries,
    sourceDetails,
    getContactTopicsKey(result.projectId),
  );
  addStorageEntry(entries, sourceDetails, getDismissedKey(result.projectId));
  addStorageEntry(
    entries,
    sourceDetails,
    getGraphExpandedKey(result.projectId),
  );
  addStorageEntry(entries, sourceDetails, langKey(result.projectId));
  addSourceEntry(entries, sourceDetails, result.projectDataSource);
  addSourceEntry(entries, sourceDetails, activeSource?.source);

  if (activeSource?.mergeCheckCache) {
    addStorageEntry(
      entries,
      sourceDetails,
      getMergeCheckResultCacheKey(activeSource.mergeCheckCache.mergeCacheKey),
    );
    addMergeStageEntries(entries, sourceDetails, activeSource.mergeCheckCache);
    addSourceEntry(
      entries,
      sourceDetails,
      activeSource.mergeCheckCache.baseSource,
    );
    addSourceEntry(
      entries,
      sourceDetails,
      activeSource.mergeCheckCache.candidateSource,
    );
  }

  setStorageEntry(
    entries,
    resultKey,
    JSON.stringify({ v: RESULTS_CACHE_VERSION, data: result }),
  );

  if (freeformReviewResult) {
    setStorageEntry(
      entries,
      freeformReviewStorageKey(freeformReviewResult, {
        projectId: result.projectId,
        source: activeSource?.source ?? result.projectDataSource,
      }),
      JSON.stringify({
        v: FREEFORM_RESULT_CACHE_VERSION,
        data: freeformReviewResult,
      }),
    );
  }

  if (result.contactTopicsResult) {
    setStorageEntry(
      entries,
      getContactTopicsKey(result.projectId),
      JSON.stringify({
        v: CONTACT_TOPICS_VERSION,
        data: result.contactTopicsResult,
      }),
    );
  }

  return {
    format: BUNDLE_FORMAT,
    version: BUNDLE_VERSION,
    exportedAt: new Date().toISOString(),
    projectId: result.projectId,
    language: result.language,
    result,
    source: result.projectDataSource,
    mergeCheckCache: activeSource?.mergeCheckCache,
    freeformReviewResult,
    storageEntries: entries,
    historyEntry: buildHistoryEntry(result),
    sourceDetails: await collectStoredSourceDetails(entries, sourceDetails),
  };
}

export async function downloadAICheckShareBundle(
  bundle: AICheckShareBundle,
): Promise<void> {
  const sourceLabel =
    bundle.source?.label?.replace(/[^\w.-]+/g, "_").slice(0, 80) ||
    `project_${bundle.projectId}`;
  const jsonFileName = `aicheck_${sourceLabel}_${bundle.language}_${bundle.exportedAt.slice(0, 10)}.json`;
  const blob = await createSingleJsonZipBlob(
    jsonFileName,
    `${JSON.stringify(bundle, null, 2)}\n`,
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${jsonFileName}.zip`;
  link.click();
  URL.revokeObjectURL(url);
}

function parseBundle(raw: unknown): AICheckShareBundle {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid AI check bundle.");
  }
  const bundle = raw as Partial<AICheckShareBundle>;
  if (bundle.format !== BUNDLE_FORMAT || bundle.version !== BUNDLE_VERSION) {
    throw new Error("Unsupported AI check bundle format.");
  }
  if (
    !bundle.result ||
    typeof bundle.projectId !== "number" ||
    (bundle.language !== "en" && bundle.language !== "ja") ||
    !Array.isArray(bundle.storageEntries)
  ) {
    throw new Error("AI check bundle is missing required data.");
  }
  return bundle as AICheckShareBundle;
}

async function readAICheckShareBundleFile(
  file: File,
): Promise<AICheckShareBundle> {
  const text = (await isZipArchiveFile(file))
    ? await readSingleJsonFromZipFile(file)
    : await file.text();
  return parseBundle(JSON.parse(text));
}

export async function previewAICheckShareBundleFile(
  file: File,
): Promise<AICheckShareBundleImportPreview> {
  const bundle = await readAICheckShareBundleFile(file);
  const plan = buildBundleRestorePlan(bundle);
  const overwrites = await findBundleOverwrites(bundle);
  const freeformReviewResult = freeformReviewResultFromBundle(bundle);
  return {
    fileName: file.name,
    projectId: bundle.projectId,
    language: bundle.language,
    projectName: projectNameFromResult(bundle.result),
    sourceLabel: bundle.source?.label ?? bundle.result.projectDataSource?.label,
    completedAt: bundle.result.completedAt,
    exportedAt: bundle.exportedAt,
    storageEntryCount: plan.storageEntries.length,
    sourceDetailCount: plan.sourceDetails.length,
    freeformReviewResultCount: freeformReviewResult ? 1 : 0,
    overwriteCount: overwrites.length,
    overwrites,
  };
}

export async function importAICheckShareBundleFile(
  file: File,
  options: AICheckShareBundleImportOptions = {},
): Promise<AICheckShareBundleRestoreResult> {
  const bundle = await readAICheckShareBundleFile(file);
  const overwrites = await findBundleOverwrites(bundle);
  if (overwrites.length > 0 && !options.overwrite) {
    throw new Error(
      "This share bundle would overwrite existing cache. Confirm overwrite before importing.",
    );
  }

  const plan = buildBundleRestorePlan(bundle);
  const freeformReviewResult = freeformReviewResultFromBundle(bundle);
  let restoredEntryCount = 0;
  let restoredDetailCount = 0;

  for (const entry of plan.storageEntries) {
    localStorage.setItem(entry.key, entry.value);
    restoredEntryCount += 1;
  }

  for (const detail of plan.sourceDetails) {
    try {
      await writeProjectDataSourceDetails(detail);
      restoredDetailCount += 1;
    } catch (error) {
      console.warn(
        "[aicheck-share-bundle] Failed to restore source details",
        error,
      );
    }
  }

  const fallbackHistoryEntry = buildHistoryEntry(bundle.result);
  mergeHistoryEntry({
    ...fallbackHistoryEntry,
    ...(bundle.historyEntry ?? {}),
    projectTitle:
      bundle.historyEntry?.projectTitle ?? fallbackHistoryEntry.projectTitle,
    sourceLabel:
      bundle.historyEntry?.sourceLabel ?? fallbackHistoryEntry.sourceLabel,
    sourceKind:
      bundle.historyEntry?.sourceKind ?? fallbackHistoryEntry.sourceKind,
    sourceCacheKey:
      bundle.historyEntry?.sourceCacheKey ??
      fallbackHistoryEntry.sourceCacheKey,
    usesExternalProjectData:
      bundle.historyEntry?.usesExternalProjectData ??
      fallbackHistoryEntry.usesExternalProjectData,
  });
  syncProjectCacheInventory();

  return {
    projectId: bundle.projectId,
    language: bundle.language,
    restoredEntryCount,
    restoredDetailCount,
    result: bundle.result,
    mergeCheckCache: bundle.mergeCheckCache,
    freeformReviewResult,
  };
}
